import React, { useState, useEffect, useCallback, useRef } from "react";
import apiService from "../../../services/api";
import { useNavigate } from "react-router-dom";

const formatTemplateDate = (value) => {
  if (!value) return "Just now";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const truncateContent = (content, maxLength = 150) => {
  if (!content || typeof content !== "string") return "";
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength).trim() + "...";
};

const SystemPromptListGrid = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const hasSeeded = useRef(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.listPromptTemplates({
        category: "system",
        pageSize: 100,
        orderBy: "Name",
        orderDir: "ASC",
      });

      if (response?.success) {
        const templatesData = response.data?.templates ?? [];
        const normalized = templatesData.map((t) => ({
          id: t.templateId || t.id,
          name: t.name,
          version: t.version,
          content: t.content,
          isActive: Boolean(t.isActive),
          promptKey: t.promptKey,
          category: t.category,
          updatedAt: formatTemplateDate(t.changeDate || t.createDate),
        }));
        setTemplates(normalized);

        const backendTotal = Number(response.data?.totalCount);
        setTotalCount(
          Number.isFinite(backendTotal) ? backendTotal : normalized.length
        );
      } else {
        setError(response?.message || "Failed to load system prompts");
      }
    } catch (err) {
      console.error("Error loading system prompts:", err);
      setError(err.response?.data?.message || "Failed to load system prompts");
    } finally {
      setLoading(false);
    }
  }, []);

  const seedAndLoad = useCallback(async () => {
    if (hasSeeded.current) {
      await loadTemplates();
      return;
    }

    try {
      setSeeding(true);
      setError(null);
      await apiService.seedSystemPrompts();
      hasSeeded.current = true;
    } catch (err) {
      console.error("Error seeding system prompts:", err);
      setError("Failed to sync system prompts from GraphBuilder");
    } finally {
      setSeeding(false);
    }

    await loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    seedAndLoad();
  }, [seedAndLoad]);

  const countLabel =
    loading || seeding
      ? "Syncing..."
      : `${totalCount ?? templates.length} system prompts`;

  return (
    <div className="user-home-layout__card">
      <div className="user-home-layout__templates-meta">
        <span className="user-home-layout__badge user-home-layout__badge--info">
          {countLabel}
        </span>
        <p className="user-home-layout__card-text user-home-layout__card-text--muted">
          System prompts drive the core pipeline: graph extraction, compilation,
          query processing, and answer generation. Edit a prompt to customize
          system-wide behavior.
        </p>
      </div>

      {error && (
        <p
          className="user-home-layout__card-text"
          style={{ color: "#b91c1c" }}
        >
          {error}
        </p>
      )}

      {(loading || seeding) && templates.length === 0 ? (
        <p className="user-home-layout__card-text user-home-layout__card-text--muted">
          {seeding
            ? "Syncing system prompts from GraphBuilder..."
            : "Loading system prompts..."}
        </p>
      ) : !loading && templates.length === 0 && !error ? (
        <p className="user-home-layout__card-text user-home-layout__card-text--muted">
          No system prompts found. Ensure GraphBuilder is running and try
          refreshing.
        </p>
      ) : templates.length > 0 ? (
        <div className="user-home-layout__templates-grid">
          {templates.map((template) => (
            <article
              key={template.id}
              className="user-home-layout__template-card"
            >
              <header className="user-home-layout__template-card-header">
                <h2 className="user-home-layout__template-title">
                  {template.name}
                </h2>
                <div className="user-home-layout__template-tags">
                  <span className="user-home-layout__template-badge">
                    v{template.version}
                  </span>
                  <span
                    className={`user-home-layout__template-status ${
                      template.isActive
                        ? "user-home-layout__template-status--active"
                        : "user-home-layout__template-status--inactive"
                    }`}
                  >
                    {template.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </header>

              {template.promptKey && (
                <span className="prompt-templates-page__prompt-key-badge">
                  {template.promptKey}
                </span>
              )}

              <p className="user-home-layout__template-content">
                {truncateContent(template.content)}
              </p>

              <footer className="user-home-layout__template-footer">
                <span className="user-home-layout__template-updated">
                  Updated {template.updatedAt}
                </span>
                <button
                  type="button"
                  className="user-home-layout__action-button user-home-layout__action-button--primary user-home-layout__template-action"
                  onClick={() =>
                    navigate(`/user-home/prompt-templates/${template.id}`)
                  }
                >
                  View
                </button>
              </footer>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SystemPromptListGrid;
